'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { FUTURE_KEYS, type WatchlistSource, type PanelSort } from '@/lib/watchlistPanel';

const LS_KEY = 'watchlistPanel.v1';

export type FuturesPosition = 'off' | 'top' | 'bottom';

export interface WatchlistPanelConfig {
  /** Row order. */
  sort: PanelSort;
  /** Show per-row logos (falls back to an initial avatar when missing). */
  showLogos: boolean;
  /** Flash a row red when its % change crosses from up to down on refresh. */
  flashOnChange: boolean;
  /** Where the futures group sits, or 'off' to hide it. */
  futuresPosition: FuturesPosition;
  /** Which futures (FUTURES keys) to show when the group is on. */
  futuresSelected: string[];
}

export const DEFAULT_CONFIG: WatchlistPanelConfig = {
  sort: 'alpha',
  showLogos: true,
  flashOnChange: false,
  futuresPosition: 'off',
  futuresSelected: [...FUTURE_KEYS],
};

/** Merge persisted config onto defaults and drop invalid values (e.g. the
 *  removed 'manual'/'list' sort, or a stale boolean includeFutures). */
function sanitizeConfig(raw: unknown): WatchlistPanelConfig {
  const c = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const sort: PanelSort = c.sort === 'change' ? 'change' : 'alpha';
  const pos = c.futuresPosition;
  const futuresPosition: FuturesPosition = pos === 'top' || pos === 'bottom' ? pos : 'off';
  const sel = Array.isArray(c.futuresSelected)
    ? c.futuresSelected.filter((k): k is string => typeof k === 'string' && (FUTURE_KEYS as readonly string[]).includes(k))
    : [...FUTURE_KEYS];
  return {
    sort,
    showLogos: typeof c.showLogos === 'boolean' ? c.showLogos : true,
    flashOnChange: typeof c.flashOnChange === 'boolean' ? c.flashOnChange : false,
    futuresPosition,
    futuresSelected: sel.length > 0 ? sel : [...FUTURE_KEYS],
  };
}

/** Panel width is shared so the layout can reserve matching space (and the
 *  chart reflows) when the user drag-resizes the panel. 208px (old fixed
 *  width) is the minimum. */
export const PANEL_MIN_WIDTH = 208;
export const PANEL_MAX_WIDTH = 700;

/** What the page chart currently displays, published so the GLOBAL side panel's
 *  Trade tab can prefill the price + (for replay/historical) the as-of time —
 *  the panel only knows the URL symbol, not the chart's live/replay price.
 *  `asOfIso` set ⇒ a paused-replay / historical-day price (trade time = then);
 *  null ⇒ the live quote (trade time = now). Transient (not persisted). */
export interface TradePrefill {
  symbol: string;
  price: number | null;
  asOfIso: string | null;
  asOfLabel: string | null;
}

/** A one-shot "the user just manually refreshed the price" signal, so the
 *  global panel's Live-price-action tab can append a tick immediately instead
 *  of waiting for its next auto-poll. `at` is the click time (also the de-dupe
 *  key). Transient (not persisted). */
export interface PricePulse {
  symbol: string;
  price: number;
  at: number;
}

interface WatchlistPanelValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  source: WatchlistSource;
  setSource: (s: WatchlistSource) => void;
  config: WatchlistPanelConfig;
  setConfig: (patch: Partial<WatchlistPanelConfig>) => void;
  width: number;
  setWidth: (w: number) => void;
  tradePrefill: TradePrefill | null;
  setTradePrefill: (p: TradePrefill | null) => void;
  pricePulse: PricePulse | null;
  setPricePulse: (p: PricePulse | null) => void;
  /** The active detail chart's effective auto-refresh cadence (ms), published so
   *  the Live-price-action tab polls in lockstep with the chart. Null = unset
   *  (e.g. no detail page mounted) → the panel uses its own default. Transient. */
  refreshMs: number | null;
  setRefreshMs: (ms: number | null) => void;
}

const WatchlistPanelContext = createContext<WatchlistPanelValue | null>(null);

export function WatchlistPanelProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSourceState] = useState<WatchlistSource>('all');
  const [config, setConfigState] = useState<WatchlistPanelConfig>(DEFAULT_CONFIG);
  const [width, setWidthState] = useState(PANEL_MIN_WIDTH);
  // Transient (not persisted) — the page chart's current price/as-of for the
  // global panel's Trade tab.
  const [tradePrefill, setTradePrefill] = useState<TradePrefill | null>(null);
  const [pricePulse, setPricePulse] = useState<PricePulse | null>(null);
  const [refreshMs, setRefreshMs] = useState<number | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { isOpen?: boolean; source?: string; config?: Partial<WatchlistPanelConfig>; width?: number };
      if (typeof parsed.isOpen === 'boolean') setIsOpen(parsed.isOpen);
      if (typeof parsed.source === 'string') setSourceState(parsed.source);
      if (parsed.config) setConfigState(sanitizeConfig(parsed.config));
      if (typeof parsed.width === 'number' && Number.isFinite(parsed.width)) {
        setWidthState(Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, parsed.width)));
      }
    } catch { /* ignore corrupt state */ }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ isOpen, source, config, width })); } catch { /* quota / private mode */ }
  }, [isOpen, source, config, width]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((o) => !o), []);
  const setSource = useCallback((s: WatchlistSource) => setSourceState(s), []);
  const setConfig = useCallback((patch: Partial<WatchlistPanelConfig>) => setConfigState((c) => ({ ...c, ...patch })), []);
  const setWidth = useCallback((w: number) => setWidthState(Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, w))), []);

  return (
    <WatchlistPanelContext.Provider value={{ isOpen, open, close, toggle, source, setSource, config, setConfig, width, setWidth, tradePrefill, setTradePrefill, pricePulse, setPricePulse, refreshMs, setRefreshMs }}>
      {children}
    </WatchlistPanelContext.Provider>
  );
}

export function useWatchlistPanel(): WatchlistPanelValue {
  const ctx = useContext(WatchlistPanelContext);
  if (!ctx) throw new Error('useWatchlistPanel must be used within WatchlistPanelProvider');
  return ctx;
}
